import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './TopPanel.css';
import {
  MoveRight,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useApi } from '../../api/api';
import TopPanelEditControls from '../TopPanelEditControls/TopPanelEditControls';

const MODES = {
  SELECTING: 'selecting',
  LOADING: 'loading',
};

/** Map module IDs to accordion category keys. Used when parent controls accordion state from module selection. */
export const MODULE_TO_ACCORDION_CATEGORIES = {
  'aop': ['stockAssessmentProjections'],
  'stock-assessment': ['stockAssessmentProjections'],
  'ssc': ['stockAssessmentProjections'],
  'pdt': ['stockAssessmentProjections', 'managementParameters', 'secondaryManagementFactors'],
  'ap': ['managementParameters'],
  'comm': ['managementParameters'],
  'council': ['managementParameters'],
  'sarc': ['stockAssessmentProjections'],
  'sas-tc': ['stockAssessmentProjections'],
  'mgmt-board': ['managementParameters', 'secondaryManagementFactors'],
  'lec': ['managementParameters'],
  'policy-board': ['managementParameters'],
};

export function getCategoriesForModule(moduleId) {
  return MODULE_TO_ACCORDION_CATEGORIES[moduleId] || [];
}

/** Build accordion state from module selection. Pass to parent via onModuleSelect(moduleId, accordionState). */
export function getAccordionStateForModule(moduleId) {
  if (!moduleId) {
    return {
      stockAssessmentProjections: false,
      secondaryManagementFactors: false,
      managementParameters: false,
      oceanDynamics: false,
    };
  }
  const categories = getCategoriesForModule(moduleId);
  return {
    stockAssessmentProjections: categories.includes('stockAssessmentProjections'),
    managementParameters: categories.includes('managementParameters'),
    secondaryManagementFactors: categories.includes('secondaryManagementFactors'),
    oceanDynamics: false,
  };
}

function ModuleButton({module, isModuleSelected, currentMode, MODES, clickModuleButton}) {
  const isSelected = isModuleSelected(module.id);
  // Only use isSelected for highlighting, not the active property
  // The active property is for indicating accordion state, not selection
  
  return (
    <button 
    className={'module-button ' 
      + (isSelected ? 'module-button-selected ' : '')
      + (currentMode===MODES.LOADING ? 'module-button-disabled ' : '')
    }
    onClick={() => clickModuleButton(module.id)}
    disabled={currentMode===MODES.LOADING}>
      <h2 className="module-button-title">{module.name}</h2>
    </button>
  )
}

const TopPanel = ({modules, onModuleSelect, selectedModuleId: externalSelectedModuleId, stockImageUrl: stockImageUrlProp, editMode = false}) => {
  // Ref for the modules container to enable scrolling to selected module
  const modulesContainerRef = useRef(null);
  const { regionSlug, planSlug, stockSlug } = useParams();
  const { getStocksBySlugs } = useApi();
  const [stockImageUrlFetched, setStockImageUrlFetched] = useState(null);

  // Use prop when provided (from Stock page); otherwise fall back to fetched value (e.g. Plan/Region with modules)
  const stockImageUrl = stockImageUrlProp !== undefined ? stockImageUrlProp : stockImageUrlFetched;

  // Use external selectedModuleId if provided, otherwise use internal state
  const [internalSelectedModule, setInternalSelectedModule] = useState();
  const selectedModule = externalSelectedModuleId !== undefined ? externalSelectedModuleId : internalSelectedModule;
  const [currentMode, setCurrentMode] = useState(MODES.SELECTING);
  const [componentSlugsByModule, setComponentSlugsByModule] = useState({});

  const handleComponentSlugsChange = useCallback((pageId, slugs) => {
    setComponentSlugsByModule((prev) => ({ ...prev, [pageId]: slugs }));
  }, []);

  // Fetch stock image URL only when not provided by parent (e.g. Plan/Region pages with modules)
  useEffect(() => {
    if (stockImageUrlProp !== undefined) return;
    if (!regionSlug || !planSlug || !stockSlug) {
      setStockImageUrlFetched(null);
      return;
    }
    let cancelled = false;
    const fetchStock = async () => {
      try {
        const response = await getStocksBySlugs(stockSlug, planSlug, regionSlug);
        const stock = response?.data;
        const url = stock?.stock_image ?? stock?.image_url ?? null;
        if (!cancelled) setStockImageUrlFetched(url);
      } catch {
        if (!cancelled) setStockImageUrlFetched(null);
      }
    };
    fetchStock();
    return () => { cancelled = true; };
  }, [stockImageUrlProp, regionSlug, planSlug, stockSlug, getStocksBySlugs]);

  function changeModuleFocusNext() {
    let currentModuleIndex = modules.findIndex((module) => module.id===selectedModule);
    let nextModuleIndex = currentModuleIndex+1;
    if (nextModuleIndex > modules.length-1) nextModuleIndex = 0;
    const nextModuleId = modules[nextModuleIndex].id;
    
    // If using external control, notify parent; otherwise update internal state
    if (externalSelectedModuleId !== undefined) {
      if (onModuleSelect) {
        const accordionState = getAccordionStateForModule(nextModuleId);
        onModuleSelect(nextModuleId, accordionState);
      }
    } else {
      setInternalSelectedModule(nextModuleId);
    }
  }

  function changeModuleFocusPrevious() {
    let currentModuleIndex = modules.findIndex((module) => module.id===selectedModule);
    let previousModuleIndex = currentModuleIndex-1;
    if (previousModuleIndex < 0) previousModuleIndex = modules.length-1;
    const previousModuleId = modules[previousModuleIndex].id;
    
    // If using external control, notify parent; otherwise update internal state
    if (externalSelectedModuleId !== undefined) {
      if (onModuleSelect) {
        const accordionState = getAccordionStateForModule(previousModuleId);
        onModuleSelect(previousModuleId, accordionState);
      }
    } else {
      setInternalSelectedModule(previousModuleId);
    }
  }

  function toggleSelectModule(moduleIdToToggle) {
    if (selectedModule===moduleIdToToggle) {
      // Only update internal state if we're managing it ourselves
      if (externalSelectedModuleId === undefined) {
        setInternalSelectedModule(null);
      }
      // Notify parent that module was deselected
      if (onModuleSelect) {
        const accordionState = getAccordionStateForModule(null);
        onModuleSelect(null, accordionState);
      }
    } else {
      // Only update internal state if we're managing it ourselves
      if (externalSelectedModuleId === undefined) {
        setInternalSelectedModule(moduleIdToToggle);
      }
      // Notify parent that module was selected
      if (onModuleSelect) {
        const accordionState = getAccordionStateForModule(moduleIdToToggle);
        onModuleSelect(moduleIdToToggle, accordionState);
      }
    }
  }

  function clickModuleButton(moduleId) {
    switch (currentMode) {
      case MODES.SELECTING:
        toggleSelectModule(moduleId);
        break;
      default:
        break;
    }
  }

  function isModuleSelected(moduleId) {
    return selectedModule===moduleId;
  }
  
  // Scroll selected module into view when it changes
  useEffect(() => {
    if (!selectedModule || !modulesContainerRef.current) return;
    
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      // Find the selected module button
      const selectedButton = modulesContainerRef.current?.querySelector(
        `.module-button.module-button-selected`
      );
      
      if (selectedButton && modulesContainerRef.current) {
        // Scroll the selected button into view smoothly, centered in the container
        selectedButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    });
  }, [selectedModule]);

  return (
    <div className="noscroll-modules-panel">
      <div className="noscroll-modules-panel-row">
        {stockImageUrl && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              padding: '4px',
              borderRadius: '6px',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              flexShrink: 0,
              marginRight: '8px',
              maxWidth: '68px',
              maxHeight: '48px'
            }}
          >
            <img 
              src={stockImageUrl} 
              alt="Stock"
              className="stock-image"
              style={{
                maxWidth: '60px',
                maxHeight: '40px',
                height: 'auto',
                width: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </div>
        )}
        <button
          className='module-nav-arrow module-nav-arrow-left'
          onClick={() => {changeModuleFocusPrevious()}}>
          <ChevronLeft />
        </button>
        <div className="noscroll-modules-container" ref={modulesContainerRef}>
          {modules.map((module, index) => (
            <React.Fragment key={module.id}>
              <ModuleButton
                module={module}
                isModuleSelected={isModuleSelected}
                currentMode={currentMode}
                selectedModule={selectedModule}
                MODES={MODES}
                clickModuleButton={clickModuleButton}
                expanded={currentMode!==MODES.SIMULATION}
              />
              {index < modules.length - 1 && (
                <MoveRight className="text-gray-500" size={20} />
              )}
            </React.Fragment>
          ))}
        </div>
        <button
          className='module-nav-arrow module-nav-arrow-right'              
          onClick={() => {(changeModuleFocusNext())}}>
          <ChevronRight />
        </button>
      </div>
      {editMode && (
        <TopPanelEditControls
          modules={modules}
          selectedModuleId={selectedModule}
          componentSlugsByModule={componentSlugsByModule}
          onComponentSlugsChange={handleComponentSlugsChange}
        />
      )}
    </div>
  );
};

export default TopPanel;
